import { SoldOutNightsEditor } from "@/components/soldOut/SoldOutNightsEditor";
import { Button } from "@/components/ui/Button";
import {
  isDateBookable,
  latestSelectableSeasonDate,
  soldOutDateSelectionError,
} from "@/lib/bookingSeasons";
import {
  normalizeSoldOutNights,
  splitSoldOutNightsByToday,
  todayLocal,
} from "@/lib/soldOutDates";
import { updateTouristPanelSoldOut } from "@/lib/touristPanelApi";
import { PastSoldOutNightsModal } from "@/pages/tourist-panel/components/PastSoldOutNightsModal";
import type { TouristPanelDisplay } from "@/types/touristPanel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type Props = {
  workstationCode: string;
  display: TouristPanelDisplay;
  onSaved: (display: TouristPanelDisplay) => void;
};

function soldOutSnapshot(display: TouristPanelDisplay): string {
  const nights = normalizeSoldOutNights(display.sold_out.sold_out_nights).join(",");
  return `${nights}|${display.sold_out.sold_out_message}`;
}

export function TouristPanelSoldOutEditor({
  workstationCode,
  display,
  onSaved,
}: Props) {
  const seasons = display.online_booking_seasons ?? [];
  const todayIso = todayLocal();
  const dateMin = todayIso;
  const dateMax = useMemo(
    () => latestSelectableSeasonDate(seasons, todayIso),
    [seasons, todayIso],
  );

  const isDirtyRef = useRef(false);
  const lastSyncedSnapshot = useRef(soldOutSnapshot(display));

  const [pastNights, setPastNights] = useState<string[]>(
    () => splitSoldOutNightsByToday(display.sold_out.sold_out_nights, todayIso).pastNights,
  );
  const [futureNights, setFutureNights] = useState<string[]>(
    () => splitSoldOutNightsByToday(display.sold_out.sold_out_nights, todayIso).futureNights,
  );
  const [soldOutMessage, setSoldOutMessage] = useState(display.sold_out.sold_out_message);
  const [newSoldOutDate, setNewSoldOutDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPastModal, setShowPastModal] = useState(false);

  useEffect(() => {
    const snapshot = soldOutSnapshot(display);
    if (isDirtyRef.current || snapshot === lastSyncedSnapshot.current) return;
    const split = splitSoldOutNightsByToday(display.sold_out.sold_out_nights, todayIso);
    setPastNights(split.pastNights);
    setFutureNights(split.futureNights);
    setSoldOutMessage(display.sold_out.sold_out_message);
    lastSyncedSnapshot.current = snapshot;
    setSuccess(false);
    setDateError(null);
  }, [display, todayIso]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const soldOut = await updateTouristPanelSoldOut(workstationCode, {
        sold_out_nights: futureNights,
        sold_out_message: soldOutMessage.trim() || undefined,
      });
      const nextDisplay: TouristPanelDisplay = {
        ...display,
        sold_out: soldOut,
      };
      onSaved(nextDisplay);
      const split = splitSoldOutNightsByToday(soldOut.sold_out_nights, todayIso);
      setPastNights(split.pastNights);
      setFutureNights(split.futureNights);
      lastSyncedSnapshot.current = soldOutSnapshot(nextDisplay);
      isDirtyRef.current = false;
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }, [workstationCode, futureNights, soldOutMessage, display, onSaved, todayIso]);

  function markDirty() {
    isDirtyRef.current = true;
    setSuccess(false);
  }

  function onAddSoldOutDate() {
    const selectionError = soldOutDateSelectionError(newSoldOutDate, seasons, todayIso);
    if (selectionError) {
      setDateError(selectionError);
      return;
    }
    if (dateMax && newSoldOutDate > dateMax) {
      setDateError("Das Datum liegt außerhalb der Buchungssaison.");
      return;
    }
    if (!newSoldOutDate || futureNights.includes(newSoldOutDate)) return;
    setFutureNights((prev) => [...prev, newSoldOutDate].sort());
    setNewSoldOutDate("");
    setDateError(null);
    markDirty();
  }

  function onRemoveSoldOutDate(iso: string) {
    setFutureNights((prev) => prev.filter((d) => d !== iso));
    markDirty();
  }

  const seasonHint =
    seasons.length > 0
      ? `Buchbar sind nur Daten in: ${seasons
          .slice(0, 3)
          .map((season) => season.name)
          .filter(Boolean)
          .join(", ")}${seasons.length > 3 ? " …" : ""}.`
      : null;

  return (
    <div className="mx-auto max-w-2xl space-y-6 rounded-xl border border-separator bg-fill-quaternary p-6">
      <div>
        <h1 className="text-2xl font-semibold text-label">Ausgebuchte Nächte</h1>
        <p className="mt-1 text-sm text-label-secondary">
          {display.workstation_label} · Tourist-Anbindung
        </p>
      </div>

      {pastNights.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-label-secondary">
            {pastNights.length} vergangene {pastNights.length === 1 ? "Nacht" : "Nächte"} ausgeblendet.
          </p>
          <Button type="button" variant="secondary" onClick={() => setShowPastModal(true)}>
            Vergangene Tage anzeigen
          </Button>
        </div>
      ) : null}

      <SoldOutNightsEditor
        soldOutNights={futureNights}
        soldOutMessage={soldOutMessage}
        newSoldOutDate={newSoldOutDate}
        canWrite
        dateMin={dateMin}
        dateMax={dateMax}
        dateError={dateError}
        helperText={seasonHint ?? undefined}
        onSoldOutMessageChange={(value) => {
          setSoldOutMessage(value);
          markDirty();
        }}
        onNewSoldOutDateChange={(value) => {
          setNewSoldOutDate(value);
          if (!value) {
            setDateError(null);
            return;
          }
          const selectionError = soldOutDateSelectionError(value, seasons, todayIso);
          if (selectionError) {
            setDateError(selectionError);
            return;
          }
          if (dateMax && value > dateMax) {
            setDateError("Das Datum liegt außerhalb der Buchungssaison.");
            return;
          }
          if (seasons.length > 0 && !isDateBookable(value, seasons)) {
            setDateError("Das Datum liegt außerhalb der Buchungssaison.");
            return;
          }
          setDateError(null);
        }}
        onAddSoldOutDate={onAddSoldOutDate}
        onRemoveSoldOutDate={onRemoveSoldOutDate}
      />

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {success ? <p className="text-sm text-status-green">Gespeichert.</p> : null}

      <Button type="button" disabled={saving || Boolean(dateError)} onClick={() => void onSave()}>
        {saving ? "Speichern…" : "Speichern"}
      </Button>

      <PastSoldOutNightsModal
        open={showPastModal}
        onClose={() => setShowPastModal(false)}
        pastNights={pastNights}
      />
    </div>
  );
}
